/**
 * Auth Slice
 * Manages authentication state
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getAuthToken,
  getValidAuthToken,
  setAuthToken,
  removeAuthToken,
  clearAuth,
  setUserData,
  getUserData,
  removeUserData,
  isTokenExpired,
} from '../../utils/api/index.js';
import { getErrorMessage } from '../../utils/api/errors.js';
import { invalidateCompanyProfileCache } from '../../utils/companyProfileCache.js';

/**
 * Initial state
 * Validates token expiration on initialization
 */
const getInitialState = () => {
  const token = getValidAuthToken(); // This will return null if expired
  const user = getUserData();
  
  return {
    user: user || null,
    token: token,
    isAuthenticated: !!token,
    loading: false,
    error: null,
  };
};

const initialState = getInitialState();

/**
 * Async thunk: Login
 * Note: Replace with actual login API call when available
 */
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual login API call
      // const response = await authApi.login({ email, password });
      // const { token, user } = response;
      
      // Mock response for now
      const mockResponse = {
        token: 'mock-jwt-token',
        user: {
          id: 1,
          name: 'Admin User',
          email: email,
          role: 'admin',
        },
      };

      setAuthToken(mockResponse.token);
      return mockResponse;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Login failed'));
    }
  }
);

/**
 * Async thunk: Logout
 */
export const logout = createAsyncThunk('auth/logout', async () => {
  invalidateCompanyProfileCache();
  clearAuth();
  return null;
});

/**
 * Async thunk: Get current user
 * Note: Replace with actual API call when available
 */
export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual API call
      // const response = await authApi.getCurrentUser();
      
      // Mock response for now
      const mockUser = {
        id: 1,
        name: 'Admin User',
        email: 'admin@example.com',
        role: 'admin',
      };

      return mockUser;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to get current user'));
    }
  }
);

/**
 * Async thunk: Company login
 */
export const companyLogin = createAsyncThunk(
  'auth/companyLogin',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const { companyApi } = await import('../../utils/api/index.js');
      const response = await companyApi.login({ email, password });
      
      if (response && response.data) {
        const { token, company } = response.data;
        setAuthToken(token);
        return { token, company };
      }
      
      return rejectWithValue('Invalid response from server');
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Login failed'));
    }
  }
);

/**
 * Auth slice
 */
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Set authentication token
     */
    setToken: (state, action) => {
      state.token = action.payload;
      state.isAuthenticated = !!action.payload;
      if (action.payload) {
        setAuthToken(action.payload);
      } else {
        removeAuthToken();
      }
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Initialize auth from stored token
     * Validates token expiration and loads user data
     */
    initializeAuth: (state) => {
      const token = getValidAuthToken(); // Returns null if expired
      const user = getUserData();
      
      if (token && !isTokenExpired(token)) {
        state.token = token;
        state.user = user;
        state.isAuthenticated = true;
      } else {
        // Token expired or invalid, clear everything
        clearAuth();
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.error = null;
        // Store user data in localStorage
        if (action.payload.user) {
          setUserData(action.payload.user);
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
      })

      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
        removeUserData(); // Remove user data on logout
      })

      // Get current user
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // If token is invalid, clear auth
        if (action.payload?.includes('unauthorized') || action.payload?.includes('401')) {
          state.token = null;
          state.isAuthenticated = false;
          removeAuthToken();
        }
      })

      // Company login
      .addCase(companyLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(companyLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.user = action.payload.company;
        state.isAuthenticated = true;
        state.error = null;
        // Store company data in localStorage
        if (action.payload.company) {
          setUserData(action.payload.company);
        }
      })
      .addCase(companyLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
      });
  },
});

export const { setToken, clearError, initializeAuth } = authSlice.actions;

export default authSlice.reducer;
